import React from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useAccessIds } from 'providers/Access.provider';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import C from 'utils/values/constant.util';

import CN from './ScheduleColumnMiniModal.module.scss';

const L = logger('combineDateTimeZulu'); // eslint-disable-line no-unused-vars

const MESSAGE = `${S.noEntry} You do not have permission to access this feature ${S.noEntry}`;

const VARIANT = C({
  mobileSidebar: 'mobileSidebar',
});

const ForbiddenKpiLink = ({ tag, variant }) => {
  const { t } = useTranslation();
  return (
    <Tooltip variant="slickBottom" content={MESSAGE}>
      <div
        data-bem="ForbiddenKpiLink"
        data-tag={fromAnyToTrimmed(tag)}
        className={cn({
          [CN.item]: true,
          [CN.disabled]: true,
          [CN[`${variant}Variant`]]: variant,
        })}
      >
        <div className={CN.circleBlue}>
          <Icon variant="chart" width="lg" />
        </div>
        <div className={CN.itemText}>
          <div className={CN.flexHorizontal}>
            <div className={CN.link}>
              {variant === VARIANT.mobileSidebar ? t('scheduler.header.reports') : t('scheduler.header.kpiReports')}
            </div>
            <Icon variant="info" width="sm" />
          </div>
        </div>
        <div className={CN.mobileArrow}>
          <Icon variant="arrowLeft" width="md" />
        </div>
      </div>
    </Tooltip>
  );
};

const ForbiddenCashupLink = ({ tag, variant }) => {
  const { t } = useTranslation();
  return (
    <Tooltip variant="slickBottom" content={MESSAGE}>
      <div
        data-bem="ForbiddenCashupLink"
        data-tag={fromAnyToTrimmed(tag)}
        className={cn({
          [CN.item]: true,
          [CN.disabled]: true,
          [CN[`${variant}Variant`]]: variant,
        })}
      >
        <div className={CN.circleYellow}>
          <Icon variant="poundYellow" width="lg" />
        </div>
        <div className={CN.itemText}>
          <div className={CN.flexHorizontal}>
            <div className={CN.link}>
              {variant === VARIANT.mobileSidebar ? t('scheduler.header.cashUp') : t('scheduler.header.dailyCashUp')}
            </div>
            <Icon variant="info" width="sm" />
          </div>
        </div>
        <div className={CN.mobileArrow}>
          <Icon variant="arrowLeft" width="md" />
        </div>
      </div>
    </Tooltip>
  );
};

const KpiLink = ({ tag, variant }) => {
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();
  const kpiReports = linkTo({ pattern: LINK.slnStaffReportsKpi, params: { salonId }, fault: '#' });

  return (
    <NavLink
      to={kpiReports}
      data-bem="KpiLink"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        [CN.item]: true,
        [CN[`${variant}Variant`]]: variant,
      })}
    >
      <div className={CN.circleBlue}>
        <Icon variant="chart" width="lg" />
      </div>
      <div className={CN.itemText}>
        <div className={CN.link}>
          {variant === VARIANT.mobileSidebar ? t('scheduler.header.reports') : t('scheduler.header.kpiReports')}
        </div>
      </div>
      <div className={CN.mobileArrow}>
        <Icon variant="arrowLeft" width="md" />
      </div>
    </NavLink>
  );
};

const CashupLink = ({ tag, variant }) => {
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();
  const cashupLink = linkTo({ pattern: LINK.cashup, params: { salonId }, fault: '#' });

  return (
    <NavLink
      to={cashupLink}
      data-bem="CashupLink"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        [CN.item]: true,
        [CN[`${variant}Variant`]]: variant,
      })}
    >
      <div className={CN.circleYellow}>
        <Icon variant="poundYellow" width="lg" />
      </div>
      <div className={CN.itemText}>
        <div className={CN.link}>
          {variant === VARIANT.mobileSidebar ? t('scheduler.header.cashUp') : t('scheduler.header.dailyCashUp')}
        </div>
      </div>
      <div className={CN.mobileArrow}>
        <Icon variant="arrowLeft" width="md" />
      </div>
    </NavLink>
  );
};

const ScheduleColumnMiniModal = ({
  onClickWeekView,
  onClickProfileImage,
  onClose,
  stylistId,
  isMobileViewport,
  variant,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { accessStylistId } = useAccessIds();

  const isWrongStylist = !stylistId || !accessStylistId || stylistId !== accessStylistId;

  const componentClasses = cn({
    [CN.component]: true,
    [CN.dark]: isDarkMode,
    [CN[`${variant}Variant`]]: variant,
  });

  L.debug('()', 'isWrongStylist:', isWrongStylist);

  return (
    <div className={componentClasses} data-bem="ScheduleColumnMiniModal">
      {variant === VARIANT.mobileSidebar ? (
        <div className={CN.itemsContainer}>
          {/* Week View link */}
          <div
            className={cn({
              [CN.item]: true,
              [CN.mobileSidebar]: true,
            })}
            onClick={onClickWeekView}
            role="button"
            tabIndex={0}
          >
            <div className={CN.circlePurple}>
              <Icon variant="calendarSolid" width="lg" variantInvertDark />
            </div>
            <div className={CN.itemText}>{t('scheduler.header.weekView')}</div>
            <div className={CN.mobileArrow}>
              <Icon variant="arrowLeft" width="md" />
            </div>
          </div>
          {/* Cash-up link */}
          <Permitted value={PERM.canViewDailyCashupAllStaff} tag="ScheduleColumnMiniModal__link--all">
            <CashupLink tag="ScheduleColumnMiniModal__link--cashup--all" variant="mobileSidebar" />
          </Permitted>
          <Permitted not value={PERM.canViewDailyCashupAllStaff} tag="ScheduleColumnMiniModal__link--not-all">
            {isWrongStylist ? (
              <ForbiddenCashupLink tag="ScheduleColumnMiniModal__cashup--none" variant="mobileSidebar" />
            ) : (
              <>
                <Permitted value={PERM.canViewDailyCashupPersonal} tag="ScheduleColumnMiniModal__link--own">
                  <CashupLink tag="ScheduleColumnMiniModal__link--cashup--own" variant="mobileSidebar" />
                </Permitted>

                <Permitted not value={PERM.canViewDailyCashupPersonal} tag="ScheduleColumnMiniModal__link--none">
                  <ForbiddenCashupLink tag="ScheduleColumnMiniModal__cashup--none" variant="mobileSidebar" />
                </Permitted>
              </>
            )}
          </Permitted>
          {/* KPI link */}
          <Permitted value={PERM.canViewKpiReportsAllStaff} tag="ScheduleColumnMiniModal__link--all">
            <KpiLink tag="ScheduleColumnMiniModal__link--all" variant="mobileSidebar" />
          </Permitted>
          <Permitted not value={PERM.canViewKpiReportsAllStaff} tag="ScheduleColumnMiniModal__link--not-all">
            {isWrongStylist ? (
              <ForbiddenKpiLink tag="ScheduleColumnMiniModal__link--wrong" variant="mobileSidebar" />
            ) : (
              <>
                <Permitted value={PERM.canViewKpiReportsPersonal} tag="ScheduleColumnMiniModal__link--own">
                  <KpiLink tag="ScheduleColumnMiniModal__link--own" variant="mobileSidebar" />
                </Permitted>

                <Permitted not value={PERM.canViewKpiReportsPersonal} tag="ScheduleColumnMiniModal__link--none">
                  <ForbiddenKpiLink tag="ScheduleColumnMiniModal__link--none" variant="mobileSidebar" />
                </Permitted>
              </>
            )}
          </Permitted>
          <div
            onClick={onClickProfileImage}
            className={cn({
              [CN.item]: true,
              [CN.mobileSidebar]: true,
            })}
          >
            <div className={CN.circleSlick}>
              <Icon variant="user" width="lg" selected variantInvertDark />
            </div>
            <div className={CN.itemText}>{t('scheduler.header.profileImage')}</div>
            <div className={CN.mobileArrow}>
              <Icon variant="arrowLeft" width="md" />
            </div>
          </div>
        </div>
      ) : (
        <DropdownFoldout width="sm" position="miniModal">
          <DropdownFoldout.Body>
            <div className={CN.itemsContainer}>
              {isMobileViewport && (
                <div className={CN.closeButton}>
                  <Button variant="empty" onClick={onClose}>
                    <div className={CN.brightnessContainer}>
                      <Icon variant="closeDark" width="md" />
                    </div>
                  </Button>
                </div>
              )}

              {/* Week View link */}
              <div className={CN.item} onClick={onClickWeekView} role="button" tabIndex={0}>
                <div className={CN.circlePurple}>
                  <Icon variant="calendarSolid" width="lg" variantInvertDark />
                </div>
                <div className={CN.itemText}>{t('scheduler.header.weekView')}</div>
              </div>

              {/* Cash-up link */}
              <Permitted value={PERM.canViewDailyCashupAllStaff} tag="ScheduleColumnMiniModal__link--all">
                <CashupLink tag="ScheduleColumnMiniModal__link--cashup--all" />
              </Permitted>

              <Permitted not value={PERM.canViewDailyCashupAllStaff} tag="ScheduleColumnMiniModal__link--not-all">
                {isWrongStylist ? (
                  <ForbiddenCashupLink tag="ScheduleColumnMiniModal__cashup--none" />
                ) : (
                  <>
                    <Permitted value={PERM.canViewDailyCashupPersonal} tag="ScheduleColumnMiniModal__link--own">
                      <CashupLink tag="ScheduleColumnMiniModal__link--cashup--own" />
                    </Permitted>

                    <Permitted not value={PERM.canViewDailyCashupPersonal} tag="ScheduleColumnMiniModal__link--none">
                      <ForbiddenCashupLink tag="ScheduleColumnMiniModal__cashup--none" />
                    </Permitted>
                  </>
                )}
              </Permitted>

              {/* KPI link */}
              <Permitted value={PERM.canViewKpiReportsAllStaff} tag="ScheduleColumnMiniModal__link--all">
                <KpiLink tag="ScheduleColumnMiniModal__link--all" />
              </Permitted>

              <Permitted not value={PERM.canViewKpiReportsAllStaff} tag="ScheduleColumnMiniModal__link--not-all">
                {isWrongStylist ? (
                  <ForbiddenKpiLink tag="ScheduleColumnMiniModal__link--wrong" />
                ) : (
                  <>
                    <Permitted value={PERM.canViewKpiReportsPersonal} tag="ScheduleColumnMiniModal__link--own">
                      <KpiLink tag="ScheduleColumnMiniModal__link--own" />
                    </Permitted>

                    <Permitted not value={PERM.canViewKpiReportsPersonal} tag="ScheduleColumnMiniModal__link--none">
                      <ForbiddenKpiLink tag="ScheduleColumnMiniModal__link--none" />
                    </Permitted>
                  </>
                )}
              </Permitted>

              <div className={CN.item} onClick={() => onClickProfileImage(stylistId)} role="button" tabIndex={0}>
                <div className={CN.circlePurple}>
                  <Icon variant="user" width="lg" selected variantInvertDark />
                </div>
                <div className={CN.itemText}>{t('scheduler.header.profileImage')}</div>
              </div>
            </div>
          </DropdownFoldout.Body>
        </DropdownFoldout>
      )}
    </div>
  );
};

export default ScheduleColumnMiniModal;
